import { useState, useEffect } from "react";
import { BaseURL, VendorProfile } from "../../APIs/APIUrls";
import { useSnackbar } from 'notistack';

const vendorProfileBaseUrl = VendorProfile
  .replace("://", "__PROTOCOL__")
  .replace(/\/{2,}/g, "/")
  .replace("__PROTOCOL__", "://")
  .replace(/\/+$/, "");

const getToken = () => {
  try {
    const tokenStr = localStorage.getItem("tcn");
    return tokenStr ? JSON.parse(tokenStr) : null;
  } catch (error) {
    console.error("Error parsing token:", error);
    return localStorage.getItem("tcn");
  }
};

export default function MyProfile() {

  const { enqueueSnackbar } = useSnackbar();
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState("");
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);

  const [formData, setFormData] = useState({
    VendorProfileId: "",
    VendorId: "",
    FullName: "",
    Description: "",
    Company: "",
    Position: "",
    OfficeNumber: "",
    OfficeAddress: "",
    JobProfile: "",
    Email: "",
    Phone: "",
    PersonalAddress: "",
    Facebook: "",
    Twitter: "",
    Linkdin: "",
    Photo: ""
  });



  useEffect(() => {
    const vendorId = localStorage.getItem("id");
    if (vendorId) {
      setFormData(prev => ({ ...prev, VendorId: vendorId }));
      fetchVendorProfile(vendorId);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "Phone" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const getDisplayablePhoto = (profileData) => {
    const incomingPhoto =
      profileData?.ProfilePhoto ||
      profileData?.Photo ||
      profileData?.ImageURL ||
      profileData?.ProfileImage ||
      "";

    if (!incomingPhoto) return "";
    if (incomingPhoto.startsWith("data:") || incomingPhoto.startsWith("http")) {
      return incomingPhoto;
    }
    return `${BaseURL}${incomingPhoto.startsWith("/") ? incomingPhoto : `/${incomingPhoto}`}`;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSizeInBytes = 1 * 1024 * 1024;

    if (!validMimeTypes.includes(file.type)) {
      enqueueSnackbar("Only JPG, PNG, and WEBP image files are allowed", { variant: "warning" });
      return;
    }

    if (file.size > maxSizeInBytes) {
      enqueueSnackbar("Photo size must be less than 2MB", { variant: "warning" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result || "");
      setSelectedPhotoFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setSelectedPhotoFile(null);
    const existingPhoto = getDisplayablePhoto({ Photo: formData.Photo });
    setPhotoPreview(existingPhoto);
  };

  const validateForm = () => {
    const nextErrors = {};
    const fullName = (formData.FullName || "").trim();
    // const email = (formData.Email || "").trim();
    const phone = (formData.Phone || "").trim();

    if (!fullName) {
      nextErrors.FullName = "Name is required";
    } else if (fullName.length <= 3) {
      nextErrors.FullName = "Name must be greater than 3 characters";
    }

    // if (!email) {
    //   nextErrors.Email = "Email is required";
    // } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    //   nextErrors.Email = "Please enter a valid email address";
    // }

    if (!phone) {
      nextErrors.Phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(phone)) {
      nextErrors.Phone = "Mobile number must be exactly 10 digits";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const [loading, setLoading] = useState(false);



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar("Please fix the validation errors", { variant: "warning" });
      return;
    }

    setLoading(true);
    const vendorId = localStorage.getItem("id");

    try {
      const vendorProfilePayload = {
        VendorProfileId: formData.VendorProfileId || 0,
        VendorId: vendorId,
        FullName: formData.FullName,
        Description: formData.Description,
        Company: formData.Company,
        Position: formData.Position,
        OfficeNumber: formData.OfficeNumber,
        OfficeAddress: formData.OfficeAddress,
        JobProfile: formData.JobProfile,
        Phone: formData.Phone,
        PersonalAddress: formData.PersonalAddress,
        Facebook: formData.Facebook,
        Twitter: formData.Twitter,
        Linkdin: formData.Linkdin,
        Photo: formData.Photo || ""
      };

      const tokenStr = getToken();
      const isUpdate = hasExistingProfile && Number(vendorProfilePayload.VendorProfileId) > 0;
      const endpoint = isUpdate
        ? `UpdateVendorProfile/${vendorProfilePayload.VendorProfileId}`
        : "InsertVendorProfile";

      let response;
      if (isUpdate) {
        const multipartFormData = new FormData();
        multipartFormData.append("profileJson", JSON.stringify(vendorProfilePayload));
        if (selectedPhotoFile) {
          multipartFormData.append("photo", selectedPhotoFile);
        }

        response = await fetch(`${vendorProfileBaseUrl}/${endpoint}`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${tokenStr}`
          },
          body: multipartFormData
        });
      } else {
        response = await fetch(`${vendorProfileBaseUrl}/${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${tokenStr}`
          },
          body: JSON.stringify(vendorProfilePayload)
        });
      }

      if (response.ok) {
        const result = await response.json();
        console.log("Profile saved:", result);
        enqueueSnackbar(hasExistingProfile ? "Profile updated successfully!" : "Profile saved successfully!", { variant: "success" });
        setSelectedPhotoFile(null);
        await fetchVendorProfile(vendorId);
      } else {
        const errorText = await response.text();
        console.error("Error saving profile:", response.status, errorText);
        enqueueSnackbar("Error saving profile!", { variant: "error" });
      }
    } catch (error) {
      console.error("API error:", error);
      enqueueSnackbar("Error saving profile!", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorProfile = async (vendorId) => {
    try {
      const tokenStr = getToken();
      const response = await fetch(`${vendorProfileBaseUrl}/GetVendorProfileById/${vendorId}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${tokenStr}`
        }
      });
      if (response.ok) {
        const res = await response.json();
        const profileData = res?.data;

        if (profileData && Object.keys(profileData).length > 0) {
          const profilePhoto = getDisplayablePhoto(profileData);
          setHasExistingProfile(true);
          setPhotoPreview(profilePhoto);
          setSelectedPhotoFile(null);
          setFormData(prev => ({
            ...prev,
            ...profileData,
            VendorProfileId: profileData.VendorProfileId || prev.VendorProfileId,
            VendorId: vendorId,
            Photo: profileData?.Photo || ""
          }));
        } else {
          setHasExistingProfile(false);
          setPhotoPreview("");
          setSelectedPhotoFile(null);
          setFormData(prev => ({ ...prev, VendorProfileId: "", VendorId: vendorId }));
        }
      }
      else {
        const errorText = await response.text();

        if (response.status === 404) {
          setHasExistingProfile(false);
          setPhotoPreview("");
          setSelectedPhotoFile(null);
          setFormData(prev => ({ ...prev, VendorProfileId: "", VendorId: vendorId }));
          return;
        }

        console.error("Error fetching profile:", response.status, errorText);
        enqueueSnackbar("Error fetching profile!", { variant: "error" });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      enqueueSnackbar("Error fetching profile!", { variant: "error" });
    }
  };

  return (
    <div className="main-content">
      <div className="main-content-inner wrap-dashboard-content-2">
        <div className="widget-box-2">
          <h5 className="title">Information</h5>

          <form onSubmit={handleSubmit}>


            <div className="box box-fieldset">
              <label>Profile Photo:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="form-control style-1"
              />
              <small style={{ display: "block", marginTop: "6px", color: "#6c757d" }}>
                Allowed formats: JPG, PNG, WEBP (max 1MB)
              </small>
              {photoPreview && (
                <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "12px" }}>
                  <img
                    src={photoPreview}
                    alt="Profile preview"
                    style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "50%", border: "1px solid #ddd" }}
                  />
                  {/* <button type="button" className="tf-btn" onClick={handleRemovePhoto}>
                    Remove Photo
                  </button> */}
                </div>
              )}
            </div>

            <div className="box box-fieldset">
              <label>Full Name:<span>*</span></label>
              <input type="text" name="FullName" value={formData.FullName} onChange={handleInputChange} className="form-control style-1" maxLength={100} />
              {errors.FullName && <small style={{ color: "#dc3545" }}>{errors.FullName}</small>}
            </div>

            <div className="box box-fieldset">
              <label>Description:</label>
              <textarea name="Description" value={formData.Description} onChange={handleInputChange} className="form-control style-1" maxLength={500} />
            </div>

            <div className="box grid-4 gap-30">
              <div className="box-fieldset">
                <label>Company:</label>
                <input type="text" name="Company" value={formData.Company} onChange={handleInputChange} className="form-control style-1" maxLength={100} />
              </div>
              <div className="box-fieldset">
                <label>Position:</label>
                <input type="text" name="Position" value={formData.Position} onChange={handleInputChange} className="form-control style-1" maxLength={100} />
              </div>
              <div className="box-fieldset">
                <label>Office Number:</label>
                <input type="text" name="OfficeNumber" value={formData.OfficeNumber} onChange={handleInputChange} className="form-control style-1" maxLength={20} />
              </div>
              <div className="box-fieldset">
                <label>Office Address:</label>
                <input type="text" name="OfficeAddress" value={formData.OfficeAddress} onChange={handleInputChange} className="form-control style-1" maxLength={200} />
              </div>
            </div>

            <div className="box grid-4 gap-30 box-info-2">
              <div className="box-fieldset">
                <label>Job Profile:</label>
                <input type="text" name="JobProfile" value={formData.JobProfile} onChange={handleInputChange} className="form-control style-1" maxLength={100} />
              </div>
              {/* <div className="box-fieldset">
                <label>Email:<span>*</span></label>
                <input type="email" name="Email" value={formData.Email} onChange={handleInputChange} className="form-control style-1" />
                {errors.Email && <small style={{ color: "#dc3545" }}>{errors.Email}</small>}
              </div> */}
              <div className="box-fieldset">
                <label>Mobile:<span>*</span></label>
                <input type="text" name="Phone" value={formData.Phone} onChange={handleInputChange} className="form-control style-1" maxLength={10} />
                {errors.Phone && <small style={{ color: "#dc3545" }}>{errors.Phone}</small>}
              </div>
              <div className="box-fieldset">
                <label>Personal Address:</label>
                <input type="text" name="PersonalAddress" value={formData.PersonalAddress} onChange={handleInputChange} className="form-control style-1" maxLength={200} />
              </div>

              <div className="box-fieldset">
                <label>Facebook:</label>
                <input type="text" name="Facebook" value={formData.Facebook} onChange={handleInputChange} className="form-control style-1" maxLength={300} />
              </div>

              <div className="box-fieldset">
                <label>Twitter:</label>
                <input type="text" name="Twitter" value={formData.Twitter} onChange={handleInputChange} className="form-control style-1" maxLength={300} />
              </div>

              <div className="box-fieldset">
                <label>Linkdin:</label>
                <input type="text" name="Linkdin" value={formData.Linkdin} onChange={handleInputChange} className="form-control style-1" maxLength={300} />
              </div>
            </div>

            <div className="box">
              <button type="submit" className="tf-btn primary" disabled={loading}

              >{loading ? "Saving..." : "Save & Update"}</button>
            </div>
          </form>
        </div>
      </div>
      <div className="footer-dashboard">
        <p>Copyright © 2024 Home Lengo</p>
      </div>
    </div>
  );
}